import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Search, Users, Settings, History, Smartphone, MessageCircle, Pause, Play, RefreshCw, Cake, Sparkles, Trash2, Plus } from 'lucide-react';
import api from '../../services/api';

const DIAS = [[1, 'Seg'], [2, 'Ter'], [3, 'Qua'], [4, 'Qui'], [5, 'Sex'], [6, 'Sáb'], [7, 'Dom']];
const ORIGENS = { corretor: 'Corretor', gestor: 'Gestor', lead: 'Funil' };
const STATUS = {
  agendado: ['Agendado', 'bg-blue-100 text-blue-700'], enviado: ['Enviado', 'bg-green-100 text-green-700'], teste: ['Teste (não enviado)', 'bg-amber-100 text-amber-700'],
  erro: ['Erro', 'bg-red-100 text-red-700'], pulado: ['Pulado', 'bg-gray-100 text-gray-500'],
};
const msgErro = (e) => e.response?.data?.error || e.message;
const fmt = (d) => (d ? new Date(d).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

function Aba({ id, atual, set, icon: Icon, children }) {
  return (
    <button onClick={() => set(id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${atual === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      <Icon size={16} />{children}
    </button>
  );
}

function Escolher() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [origem, setOrigem] = useState('todos');
  const [situacao, setSituacao] = useState('todos');
  const [soSalvos, setSoSalvos] = useState(false);
  const [marcados, setMarcados] = useState(new Set());
  const [aviso, setAviso] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['elo-pessoas'], queryFn: () => api.get('/elo/pessoas').then((r) => r.data.data) });

  const chave = (p) => `${p.origem}:${p.id}`;
  const lista = useMemo(() => (data || []).filter((p) => {
    if (busca && !`${p.nome} ${p.whatsapp} ${p.empresa || ''}`.toLowerCase().includes(busca.toLowerCase())) return false;
    if (origem !== 'todos' && (origem === 'lead' ? p.origem !== 'lead' : origem === 'gestor' ? p.tipo !== 'gestor' : !(p.origem === 'corretor' && p.tipo !== 'gestor'))) return false;
    if (situacao === 'no_elo' && !p.selecionado_id) return false;
    if (situacao === 'fora' && p.selecionado_id) return false;
    if (soSalvos && !p.salvo_no_celular) return false;
    return true;
  }), [data, busca, origem, situacao, soSalvos]);

  const sel = (data || []).filter((p) => marcados.has(chave(p)));
  const adicionar = useMutation({
    mutationFn: () => api.post('/elo/selecionados', { itens: sel.filter((p) => !p.selecionado_id).map((p) => ({ origem: p.origem, id: p.id })) }),
    onSuccess: (r) => { setAviso({ ok: true, t: `${r.data.data.adicionados} adicionado(s) ao Elo.` }); setMarcados(new Set()); qc.invalidateQueries(['elo-pessoas']); },
    onError: (e) => setAviso({ ok: false, t: msgErro(e) }),
  });
  const remover = useMutation({
    mutationFn: () => api.post('/elo/selecionados/remover', { ids: sel.filter((p) => p.selecionado_id).map((p) => p.selecionado_id) }),
    onSuccess: () => { setAviso({ ok: true, t: 'Removido(s) do Elo.' }); setMarcados(new Set()); qc.invalidateQueries(['elo-pessoas']); },
    onError: (e) => setAviso({ ok: false, t: msgErro(e) }),
  });
  const nascimento = useMutation({
    mutationFn: ({ id, data_nascimento }) => api.put(`/elo/selecionados/${id}`, { data_nascimento }),
    onSuccess: () => qc.invalidateQueries(['elo-pessoas']),
  });
  const pausar = useMutation({
    mutationFn: ({ id, ativo }) => api.put(`/elo/selecionados/${id}`, { ativo }),
    onSuccess: () => qc.invalidateQueries(['elo-pessoas']),
  });

  const alternar = (p) => setMarcados((m) => { const n = new Set(m); n.has(chave(p)) ? n.delete(chave(p)) : n.add(chave(p)); return n; });
  const todosVisiveis = lista.length > 0 && lista.every((p) => marcados.has(chave(p)));
  const noElo = (data || []).filter((p) => p.selecionado_id && p.sel_ativo).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-gray-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar nome, telefone, imobiliária" className="pl-8 pr-3 py-2 border rounded-lg text-sm w-72" />
        </div>
        <select value={origem} onChange={(e) => setOrigem(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="todos">Todos</option><option value="corretor">Corretores cadastrados</option><option value="gestor">Gestores</option><option value="lead">Leads do funil</option>
        </select>
        <select value={situacao} onChange={(e) => setSituacao(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="todos">No Elo e fora</option><option value="no_elo">Só quem está no Elo</option><option value="fora">Só quem está fora</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={soSalvos} onChange={(e) => setSoSalvos(e.target.checked)} /> Só salvos no celular</label>
        <button onClick={() => setMarcados((m) => new Set([...m, ...lista.map(chave)]))} disabled={!lista.length || todosVisiveis} className="px-3 py-2 rounded-lg border bg-white text-sm text-blue-700 disabled:opacity-40">Selecionar todos ({lista.length})</button>
        <button onClick={() => setMarcados(new Set())} disabled={!marcados.size} className="px-3 py-2 rounded-lg border bg-white text-sm text-gray-700 disabled:opacity-40">Desmarcar todos</button>
        <span className="ml-auto text-sm text-gray-500">{noElo} ativo(s) no Elo · {lista.length} na lista</span>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
          <b>{sel.length}</b> marcado(s)
          <button onClick={() => adicionar.mutate()} disabled={!sel.some((p) => !p.selecionado_id)} className="ml-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40">Adicionar ao Elo</button>
          <button onClick={() => remover.mutate()} disabled={!sel.some((p) => p.selecionado_id)} className="px-3 py-1.5 rounded-lg bg-white border text-red-600 disabled:opacity-40">Remover do Elo</button>
          <button onClick={() => setMarcados(new Set())} className="ml-auto text-gray-500">limpar</button>
        </div>
      )}
      {aviso && <div className={`text-sm px-3 py-2 rounded-lg ${aviso.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{aviso.t}</div>}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Carregando...</div> : (
          <div className="max-h-[62vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-3 w-8"><input type="checkbox" checked={todosVisiveis} onChange={() => setMarcados(todosVisiveis ? new Set() : new Set(lista.map(chave)))} /></th>
                  <th className="px-3 py-3 text-left">Nome</th><th className="px-3 py-3 text-left">WhatsApp</th><th className="px-3 py-3 text-left">Origem</th>
                  <th className="px-3 py-3 text-left">Relação</th><th className="px-3 py-3 text-left">Aniversário</th><th className="px-3 py-3 text-left">Elo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {lista.map((p) => (
                  <tr key={chave(p)} className="hover:bg-gray-50">
                    <td className="px-3 py-2"><input type="checkbox" checked={marcados.has(chave(p))} onChange={() => alternar(p)} /></td>
                    <td className="px-3 py-2 font-medium text-gray-800">{p.nome}{p.empresa && <span className="block text-xs font-normal text-gray-400">{p.empresa}</span>}</td>
                    <td className="px-3 py-2 text-gray-600">{p.whatsapp}</td>
                    <td className="px-3 py-2 text-gray-600">{p.origem === 'lead' ? ORIGENS.lead : ORIGENS[p.tipo] || 'Corretor'}{p.estagio_funil && <span className="block text-xs text-gray-400">{p.estagio_funil.replace(/_/g, ' ')}</span>}</td>
                    <td className="px-3 py-2">
                      {p.salvo_no_celular
                        ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700"><Smartphone size={12} />salvo no celular</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="Número não salvo na agenda: maior risco de bloqueio">número novo</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.selecionado_id ? (
                        <input type="date" value={p.data_nascimento || p.nascimento_cadastro || ''} title={!p.data_nascimento && p.nascimento_cadastro ? 'Vem do cadastro do corretor' : 'Data de nascimento'}
                          onChange={(e) => nascimento.mutate({ id: p.selecionado_id, data_nascimento: e.target.value })} className="border rounded px-2 py-1 text-xs" />
                      ) : <span className="text-gray-300">{p.nascimento_cadastro ? p.nascimento_cadastro.split('-').reverse().slice(0, 2).join('/') : '—'}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.selecionado_id ? (
                        <span className="inline-flex items-center gap-2">
                          {p.opt_out_em ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">pediu para sair</span>
                            : p.sel_ativo ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">no Elo</span>
                              : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">pausado</span>}
                          {!p.opt_out_em && <button title={p.sel_ativo ? 'Pausar' : 'Retomar'} onClick={() => pausar.mutate({ id: p.selecionado_id, ativo: !p.sel_ativo })} className="text-gray-400 hover:text-gray-700">{p.sel_ativo ? <Pause size={14} /> : <Play size={14} />}</button>}
                          {p.respostas > 0 && <span className="text-xs text-gray-500" title="respostas ao Elo">💬 {p.respostas}</span>}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
                {!lista.length && <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Ninguém encontrado</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Agenda() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['elo-painel'], queryFn: () => api.get('/elo/painel').then((r) => r.data.data), refetchInterval: 30000 });
  const [aviso, setAviso] = useState(null);
  const replanejar = useMutation({
    mutationFn: () => api.post('/elo/replanejar'),
    onSuccess: (r) => { const d = r.data.data; setAviso(d.planejados ? `${d.planejados} envio(s) planejado(s) para hoje.${d.previa ? ' Escrevendo as mensagens de prévia...' : ''}` : `Nada planejado: ${d.motivo}.`); qc.invalidateQueries(['elo-painel']); if (d.previa) [8000, 20000, 40000, 70000].forEach((ms) => setTimeout(() => qc.invalidateQueries(['elo-painel']), ms)); },
    onError: (e) => setAviso(msgErro(e)),
  });
  const card = (t, v, sub) => <div className="bg-white rounded-xl shadow p-4"><div className="text-xs text-gray-500 uppercase tracking-wider">{t}</div><div className="text-2xl font-bold text-gray-800 mt-1">{v ?? '—'}</div>{sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}</div>;
  const h = data?.hoje || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {card('No Elo', data?.selecionados?.ativos, `${data?.selecionados?.saiu || 0} pediram para sair`)}
        {card('Hoje', (h.agendado || 0) + (h.enviado || 0) + (h.teste || 0), `${h.enviado || 0} enviados · ${h.agendado || 0} na fila`)}
        {card('Semana', data?.semana?.enviados, 'mensagens (envio ou teste)')}
        {card('Respostas na semana', data?.semana?.respostas, 'corretores que responderam')}
      </div>
      {!!data?.aniversarios?.length && (
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"><Cake size={16} className="text-pink-500" />Próximos aniversários (30 dias)</div>
          <div className="flex flex-wrap gap-2">{data.aniversarios.map((a, i) => (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-pink-50 text-pink-700">{a.faltam === 0 ? 'hoje' : a.dia} · {a.nome}</span>
          ))}</div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button onClick={() => replanejar.mutate()} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border hover:bg-gray-50"><RefreshCw size={14} />Replanejar hoje</button>
        {aviso && <span className="text-sm text-gray-600">{aviso}</span>}
      </div>
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3 text-left">Quando</th><th className="px-4 py-3 text-left">Corretor</th><th className="px-4 py-3 text-left">Mensagem</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Resposta</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(data?.recentes || []).map((e) => {
              const [rot, cor] = STATUS[e.status] || STATUS.pulado;
              return (
                <tr key={e.id} className="align-top">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmt(e.enviado_em || e.agendado_para)}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{e.nome}<span className="block text-xs font-normal text-gray-400">{e.tema}</span></td>
                  <td className="px-4 py-3 text-gray-600 max-w-md">{e.texto || <span className="text-gray-300">{e.status === 'agendado' ? 'escrevendo prévia... (ou na hora do envio)' : '—'}</span>}{e.erro && <span className="block text-xs text-red-600">{e.erro}</span>}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${cor}`}>{rot}</span></td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs">{e.resposta ? <><MessageCircle size={12} className="inline mr-1 text-green-600" />{e.resposta}</> : <span className="text-gray-300">—</span>}</td>
                </tr>
              );
            })}
            {!data?.recentes?.length && <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-400">Nenhum envio ainda</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CartaoTema({ t, onSave, onCancel, onPrevia, onExcluir, previa }) {
    const [f, setF] = useState(t);
    const dirty = JSON.stringify(f) !== JSON.stringify(t);
    return (
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <input value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Título do tema" className="border rounded-lg px-3 py-2 text-sm font-medium flex-1 min-w-[220px]" />
          <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="border rounded-lg px-3 py-2 text-sm">
            <option value="semanal">Toque semanal</option><option value="aniversario">Aniversário</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={f.ativo} onChange={(e) => setF({ ...f, ativo: e.target.checked })} />Ativo</label>
        </div>
        <textarea rows={3} value={f.instrucao} onChange={(e) => setF({ ...f, instrucao: e.target.value })} placeholder="Instrução para a IA: o que a mensagem deve fazer (ex.: convide para o café no plantão)" className="border rounded-lg px-3 py-2 text-sm w-full" />
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => onSave(f)} disabled={!dirty || !f.titulo || !f.instrucao} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-40">Salvar</button>
          {t.id && <button onClick={() => onPrevia(t)} disabled={dirty} title={dirty ? 'Salve antes de testar' : ''} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border text-sm disabled:opacity-40"><Sparkles size={14} />Ver exemplo</button>}
          {t.id && <button onClick={() => window.confirm('Excluir este tema?') && onExcluir(t.id)} className="ml-auto text-red-500 hover:text-red-700"><Trash2 size={16} /></button>}
          {!t.id && <button onClick={onCancel} className="text-gray-500 text-sm">cancelar</button>}
        </div>
        {t.id && previa && <div className="text-sm bg-gray-50 border rounded-lg px-3 py-2 text-gray-700 whitespace-pre-wrap">{previa}</div>}
      </div>
    );
  }

function Temas() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['elo-temas'], queryFn: () => api.get('/elo/temas').then((r) => r.data.data) });
  const [novo, setNovo] = useState(null);
  const [previas, setPrevias] = useState({});
  const [aviso, setAviso] = useState(null);
  const salvar = useMutation({
    mutationFn: (t) => (t.id ? api.put(`/elo/temas/${t.id}`, t) : api.post('/elo/temas', t)),
    onSuccess: () => { setAviso({ ok: true, t: 'Tema salvo.' }); setNovo(null); qc.invalidateQueries(['elo-temas']); },
    onError: (e) => setAviso({ ok: false, t: msgErro(e) }),
  });
  const excluir = useMutation({ mutationFn: (id) => api.delete(`/elo/temas/${id}`), onSuccess: () => qc.invalidateQueries(['elo-temas']) });
  const previa = async (t) => {
    setPrevias((p) => ({ ...p, [t.id]: 'Escrevendo...' }));
    try { const r = await api.post(`/elo/temas/${t.id}/previa`, {}, { timeout: 120000 }); setPrevias((p) => ({ ...p, [t.id]: r.data.data.texto })); }
    catch (e) { setPrevias((p) => ({ ...p, [t.id]: `Falhou: ${msgErro(e)}` })); }
  };
  return (
    <div className="max-w-3xl space-y-3">
      <div className="flex items-center gap-3">
        <p className="text-sm text-gray-500 flex-1">Cada semana o Elo sorteia um tema <b>ativo</b> do tipo “toque semanal”; no dia do aniversário usa um tema de “aniversário”. A IA escreve a mensagem seguindo a instrução, sempre diferente para cada corretor.</p>
        <button onClick={() => setNovo({ titulo: '', instrucao: '', tipo: 'semanal', ativo: true })} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm"><Plus size={14} />Novo tema</button>
      </div>
      {aviso && <div className={`text-sm px-3 py-2 rounded-lg ${aviso.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{aviso.t}</div>}
      {novo && <CartaoTema key="novo" t={novo} onSave={(f) => salvar.mutate(f)} onCancel={() => setNovo(null)} />}
      {(data || []).map((t) => <CartaoTema key={`${t.id}-${t.titulo}-${t.instrucao}-${t.ativo}-${t.tipo}`} t={t} onSave={(f) => salvar.mutate(f)} onPrevia={previa} onExcluir={(id) => excluir.mutate(id)} previa={previas[t.id]} />)}
    </div>
  );
}

function Configurar() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['elo-config'], queryFn: () => api.get('/elo/config').then((r) => r.data.data) });
  const modelos = useQuery({ queryKey: ['elo-modelos'], queryFn: () => api.get('/elo/modelos').then((r) => r.data.data) });
  const [f, setF] = useState(null);
  const [aviso, setAviso] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  const salvar = useMutation({
    mutationFn: () => api.put('/elo/config', f),
    onSuccess: () => { setAviso({ ok: true, t: 'Configuração salva.' }); qc.invalidateQueries(['elo-config']); qc.invalidateQueries(['elo-painel']); },
    onError: (e) => setAviso({ ok: false, t: msgErro(e) }),
  });
  if (!f) return <div className="p-8 text-center text-gray-400">Carregando...</div>;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const campo = 'border rounded-lg px-3 py-2 text-sm w-full';
  const semanal = (f.dias_semana || []).length * f.por_dia;
  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-white rounded-xl shadow p-5 space-y-3">
        <label className="flex items-center gap-3"><input type="checkbox" checked={f.ativo} onChange={(e) => set('ativo', e.target.checked)} className="w-4 h-4" /><span className="font-medium text-gray-800">Elo ligado</span><span className="text-xs text-gray-500">(desligado: nada é planejado nem enviado)</span></label>
        <label className="flex items-center gap-3"><input type="checkbox" checked={f.modo_teste} onChange={(e) => set('modo_teste', e.target.checked)} className="w-4 h-4" /><span className="font-medium text-gray-800">Modo teste</span><span className="text-xs text-gray-500">(planeja e escreve as mensagens, mas NÃO envia pelo WhatsApp — veja na aba Agenda)</span></label>
        {!f.modo_teste && f.ativo && <div className="text-sm bg-amber-50 text-amber-800 rounded-lg px-3 py-2">Envio real ligado: as mensagens saem pela instância “{f.instancia}”.</div>}
      </div>

      <div className="bg-white rounded-xl shadow p-5 space-y-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Dias da semana em que o Elo manda mensagens</div>
          <div className="flex gap-2">{DIAS.map(([n, rot]) => {
            const on = (f.dias_semana || []).includes(n);
            return <button key={n} type="button" onClick={() => set('dias_semana', on ? f.dias_semana.filter((x) => x !== n) : [...f.dias_semana, n].sort())} className={`px-3 py-1.5 rounded-lg text-sm border ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600'}`}>{rot}</button>;
          })}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="text-sm text-gray-600">Corretores por dia<input type="number" min={1} max={40} value={f.por_dia} onChange={(e) => set('por_dia', e.target.value)} className={campo} /></label>
          <label className="text-sm text-gray-600">Das<input type="time" value={f.hora_inicio} onChange={(e) => set('hora_inicio', e.target.value)} className={campo} /></label>
          <label className="text-sm text-gray-600">Até<input type="time" value={f.hora_fim} onChange={(e) => set('hora_fim', e.target.value)} className={campo} /></label>
          <label className="text-sm text-gray-600">Intervalo mín. (seg)<input type="number" min={30} value={f.espaco_min_seg} onChange={(e) => set('espaco_min_seg', e.target.value)} className={campo} /></label>
        </div>
        <div className="text-xs text-gray-500">Capacidade: até <b>{semanal}</b> corretores por semana. Cada corretor recebe no máximo 1 mensagem por semana, em dia e horário sorteados.</div>
      </div>

      <div className="bg-white rounded-xl shadow p-5 space-y-3">
        <label className="text-sm text-gray-600 block">Instância do WhatsApp (Evolution)<input value={f.instancia} onChange={(e) => set('instancia', e.target.value)} className={campo} /></label>
        <label className="text-sm text-gray-600 block">Info do plantão (usada no convite do café)<textarea rows={2} value={f.plantao_info || ''} onChange={(e) => set('plantao_info', e.target.value)} placeholder="Ex.: estou no plantão do Lumière de segunda a sexta, das 9h às 18h" className={campo} /></label>
        <label className="text-sm text-gray-600 block">Tom das mensagens<textarea rows={2} value={f.tom || ''} onChange={(e) => set('tom', e.target.value)} className={campo} /></label>
      </div>

      {!!modelos.data?.length && <div className="text-xs text-gray-500 bg-white rounded-xl shadow p-4"><b>Cérebro da IA</b> (tenta nesta ordem, do melhor para o menor): {modelos.data.map((m) => m.replace(/^(agy|cc|devhub)\//, '$1 · ')).join(' → ')}</div>}
      <div className="flex items-center gap-3">
        <button onClick={() => salvar.mutate()} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">Salvar</button>
        {aviso && <span className={`text-sm ${aviso.ok ? 'text-green-700' : 'text-red-700'}`}>{aviso.t}</span>}
      </div>
    </div>
  );
}

export default function Elo() {
  const [aba, setAba] = useState('escolher');
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><Link2 size={24} className="text-blue-600" />Elo</h1>
        <p className="text-sm text-gray-500">Um toque por semana com os corretores que você escolher: uma mensagem diferente a cada vez, em dia e horário sorteados. Quando responderem, você assume a conversa.</p>
      </div>
      <div className="flex border-b">
        <Aba id="escolher" atual={aba} set={setAba} icon={Users}>Corretores</Aba>
        <Aba id="agenda" atual={aba} set={setAba} icon={History}>Agenda e respostas</Aba>
        <Aba id="temas" atual={aba} set={setAba} icon={Sparkles}>Temas</Aba>
        <Aba id="config" atual={aba} set={setAba} icon={Settings}>Configurar</Aba>
      </div>
      {aba === 'escolher' && <Escolher />}
      {aba === 'agenda' && <Agenda />}
      {aba === 'temas' && <Temas />}
      {aba === 'config' && <Configurar />}
    </div>
  );
}
